import React, { useEffect, useRef } from "react";
import { Plus } from "lucide-react";

/**
 * V2.0.6 / V2.0.6.1 — Tap-to-design chips backed by either:
 *   • the AI's `<SUGGEST>chip|chip|chip</SUGGEST>` block (preferred), OR
 *   • the user's PERSONALISED brainstorm set from chip-memory
 *     (`personalisedFallback`), OR
 *   • a hardcoded starter set (showFallback=true && personalised is empty).
 *
 * Each chip is sent VERBATIM to the chat handler — the AI is fluent enough
 * to parse "upgrade the door to triple-pane" without us prefixing "Add the".
 * Used by ChatPane (desktop rail), MobileMicFab (history modal), and the
 * MobileChatDock (chat-first bottom panel).
 *
 * `onVisibleChange(chips: string[])` fires whenever the visible chip list
 * changes so the caller can record "skip" events when the user sends a
 * free-form message instead of tapping one of these chips.
 */
const HARDCODED_FALLBACK_CHIPS = [
  "Suggest a few smart upgrades for this design",
  "Run a code check",
  "Estimate the energy score",
  "Add a covered porch",
  "Switch the style",
];

export default function SuggestedActionChips({
  actions = [],
  loading = false,
  onPick,
  onDismiss,
  showFallback = false,
  personalisedFallback,
  onVisibleChange,
}) {
  const personalised = Array.isArray(personalisedFallback) && personalisedFallback.length > 0
    ? personalisedFallback
    : null;
  const aiList = (actions && actions.length > 0) ? actions : null;
  const list = aiList || (showFallback ? (personalised || HARDCODED_FALLBACK_CHIPS) : []);
  const visible = list.slice(0, 5);

  // Notify the parent which chips are CURRENTLY visible so it can record
  // skip events on send. We stringify the list to avoid useEffect re-firing
  // on every render when the array identity flips but contents are equal.
  const visibleKey = visible.join("|");
  const onVisibleChangeRef = useRef(onVisibleChange);
  onVisibleChangeRef.current = onVisibleChange;
  useEffect(() => {
    onVisibleChangeRef.current?.(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey]);

  if (visible.length === 0) return null;
  const isFallback = !aiList;
  return (
    <div
      data-testid="suggested-actions"
      className="px-3 py-2 border-t border-zinc-800/80 bg-zinc-950/60"
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-[9px] uppercase tracking-widest text-brass-400/80">
          {isFallback ? "// Brainstorm" : "// Suggested Next"}
        </span>
        {onDismiss && !isFallback && (
          <button
            type="button"
            data-testid="suggested-actions-dismiss"
            onClick={onDismiss}
            className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors"
            aria-label="Dismiss suggestions"
          >
            dismiss
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {visible.map((label, i) => (
          <button
            key={`${label}-${i}`}
            type="button"
            data-testid={`suggested-action-${i}`}
            disabled={loading}
            onClick={() => onPick?.(label)}
            className={
              "inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1.5 " +
              "border border-brass-700/60 bg-brass-950/30 text-brass-300 " +
              "hover:bg-brass-900/50 hover:border-brass-500 hover:text-brass-100 " +
              "disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            }
          >
            <Plus className="w-3 h-3" />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
